import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import RenderFieldsVirtualizedAutocomplete from '../../../../../../components/Dropdown/VirtualizedAutocompleteStyled';
import {
  ArrowBack as ArrowBackIcon,
  Fullscreen,
  FullscreenExit,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import {
  Checkbox,

} from '@mui/material';
import {
  BuildingFields,
  BalanceFields,
  BuildingStatusFields,
  BuildingTypes,
  BillingAddressFields,
  AddressFields,
  DispatchTypes,
  StatusOptions,
  BillingClassOptions,
  RouteFields,

} from '../../config/formDataConfig';
import { useBuildingFormLogic } from '../../hooks/formLogic';
import ContactAccordion from '../../accordion/BuildingAccordion';
import RenderFields from '../Renders/RenderFields';
import RenderFieldsDropdown from '../Renders/RenderFieldsDropdown';
import API_BASE_URL from '../../../../../../Constants';




const BuildingEntryForm = ({ open, onClose, refreshBuildings }) => {
  const {
    buildingFormData,
    handleFieldChange,
    handleBuildingSubmit,
    customers,
    resetForm,
    fetchCustomerContacts,
    clearCustomerContacts,
    copyCutomersAddress

  } = useBuildingFormLogic();

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeAddressSection, setActiveAddressSection] = useState('address');
  const [errors, setErrors] = useState({});
  const [routeOptions, setRouteOptions] = useState([]);
  const [isNewRouteDialogOpen, setIsNewRouteDialogOpen] = useState(false);



  const [newRoute, setNewRoute] = useState({});


  useEffect(() => {

    if (buildingFormData.customerID && window.confirm('Do you want to fetch customer contacts?')) {

      fetchCustomerContacts(buildingFormData.customerID);
      copyCutomersAddress(buildingFormData.customerID);
    } else {
      clearCustomerContacts();
    }

  }, [buildingFormData.customerID]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/setup/buildings/routes`,
          {
            credentials: 'include',
          }
        );
        if (!response.ok) {
          throw new Error(`Error fetching routes: ${response.statusText}`);
        }
        const data = await response.json();
        if (data && Array.isArray(data)) {
          const options = data.map((route) => ({
            key: route.routeID,
            value: route.routeDescription || `Route ${route.routeID}`,
          }));
          setRouteOptions(options);
        }
      } catch (error) {
        console.error('Error fetching routes:', error);
      }
    };
    fetchRoutes();
  }, []);

  // Reset form data and errors when the dialog is closed
  useEffect(() => {
    if (!open) {
      resetForm();
      setErrors({});
      setActiveAddressSection('address'); // Reset to default section
    }
  }, [open]);

  // Validation logic for required fields
  const validateFields = () => {
    const requiredFields = {
      customerID: 'Customer',
      location: 'Building Name',
      buildingType: 'Building Type',
      buildingStatus: 'Building Status',
      billingClass: 'Billing Class',
      routeID: 'Route ID',
      dispatchType: 'Dispatch Type', // Added dispatchType

      // For Billing Address Fields
      billingAddressLine1: 'Billing Street',
      billingCity: 'Billing City',
      billingState: 'Billing State',
      billingZip: 'Billing Zip Code',
    };

    const newErrors = {};
    Object.keys(requiredFields).forEach((field) => {
      if (!buildingFormData[field]?.toString().trim()) {
        newErrors[field] = `${requiredFields[field]} is required.`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateFields()) {
      // Check if there are errors in the billing address fields
      const billingFields = [
        'billingAddressLine1',
        'billingCity',
        'billingState',
        'billingZip',
      ];
      const hasBillingErrors = billingFields.some((field) => errors[field]);

      if (hasBillingErrors) {
        setActiveAddressSection('billingAddress');
      }
      return;
    }

    handleBuildingSubmit(buildingFormData, () => {
      onClose();
      if (refreshBuildings) refreshBuildings();
    });
  };

  const validatedHandleFieldChange = (e) => {
    const { name, value } = e.target;

    if (name === 'state' && value.length > 2) return;
    if (name === 'zip' && (value.length > 5 || !/^\d*$/.test(value))) return;

    // For billing address fields
    if (name === 'billingState' && value.length > 2) return;
    if (name === 'billingZip' && (value.length > 5 || !/^\d*$/.test(value))) return;

    setErrors((prev) => ({ ...prev, [name]: '' }));
    handleFieldChange(e);
  };

  const newRouteHandleFieldChange = (e) => {
    const { name, value } = e.target;
    setNewRoute((prev) => ({ ...prev, [name]: value }));
  };

  // Function to generate CSS styles for error fields
  const generateErrorStyles = (errors) => {
    let styles = '';
    Object.keys(errors).forEach((fieldKey) => {
      styles += `
        input[name="${fieldKey}"],
        select[name="${fieldKey}"],
        textarea[name="${fieldKey}"],
        .MuiAutocomplete-root input[name="${fieldKey}"] {
          border-color: red !important;
        }
      `;
    });
    return styles;
  };

  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };

  // Check if there are errors in the billing address fields
  const billingFields = [
    'billingAddressLine1',
    'billingCity',
    'billingState',
    'billingZip',
  ];
  const hasBillingErrors = billingFields.some((field) => errors[field]);

  const handleSaveRoute = async () => {
    try {
      const newRouteObj = {
        routeID: newRoute.routeID || '',
        routeDescription: newRoute.routeDescription || '',
        active: newRoute.active ? 1 : 0,
        routeType: newRoute.routeType || '',
        employeeID: newRoute.employeeID || '',
        mechanic: newRoute.mechanic || '',
        managerID: newRoute.managerID || '',
        supervisorID: newRoute.supervisorID || '',
        location: newRoute.location || '',
        territory: newRoute.territory || '',
      };

      // Save the new route
      const response = await fetch(`${API_BASE_URL}/api/setup/buildings/routes/createRoute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newRouteObj),
      });

      if (response.ok) {

        setRouteOptions((prev) => [
          { key: newRouteObj.routeID, value: newRouteObj.routeDescription || `Route ${newRouteObj.routeID}` }
          , ...prev]);

        // // Automatically select the newly added route in the dropdown
        handleFieldChange({
          target: { name: 'routeID', value: newRouteObj.routeID },
        });



        // // Reset the form and clear errors

        setNewRoute({}); // Clear the new route form
        setErrors({}); // Ensure no errors are displayed
        setIsNewRouteDialogOpen(false); // Close the dialog

      } else {
        throw new Error(`Error saving route: ${response.statusText}`);
      }



    } catch (error) {
      console.error('Error saving route:', error);
    }
  };

  return (
    <>
      <style>{generateErrorStyles(errors)}</style>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isExpanded ? false : 'md'}
        fullWidth
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            height: '100%',
          },
        }}
      >
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="flex text-2xl font-bold ml-4">Add Building</h2>
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
            >
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </button>
          </div>

          {/* Submit button moved back under the title */}
          <div className="flex space-x-4 p-4">
            <button
              type="submit"
              form="buildingForm"
              className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
            >
              Submit
            </button>
          </div>
        </div>
        <DialogContent dividers>
          <form id="buildingForm" onSubmit={handleSubmit} className="space-y-1">
            {/* Building Details Section */}
            <ContactAccordion label="Building Details" expanded={isExpanded}>
              <div className="rounded-lg p-4 bg-white">

                {(() => {
                  const [leftFields, rightFields] = splitFields(BuildingFields);
                  return (
                    <div className="flex gap-4">
                      <div className="w-full">
                        <RenderFields
                          fields={leftFields}
                          formData={buildingFormData}
                          handleChange={validatedHandleFieldChange}
                        />
                        {errors['location'] && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors['location']}
                          </p>
                        )}
                      </div>
                      <div className="w-full">
                        <RenderFields
                          fields={rightFields}
                          formData={buildingFormData}
                          handleChange={validatedHandleFieldChange}
                        />
                        {errors['customerID'] && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors['customerID']}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <RenderFieldsVirtualizedAutocomplete
                  fields={[
                    {
                      key: 'buildingType',
                      label: 'Building Type',
                      options: BuildingTypes.map((type) => ({
                        key: type.key,
                        value: type.label,
                      })),
                    },
                    {
                      key: 'customerID',
                      label: 'Customer',
                      options: customers.map((customer) => ({
                        key: customer.customerID.toString(),
                        value: customer.customerName || 'No Name',
                      })),
                    },
                  ]}
                  formData={buildingFormData}
                  handleChange={validatedHandleFieldChange}
                />
                {errors['buildingType'] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors['buildingType']}
                  </p>
                )}
              </div>
            </ContactAccordion>

            {/* Address Section */}
            <ContactAccordion label="Address">
              <div className="rounded-lg p-4 bg-white">
                <div className="flex space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setActiveAddressSection('address')}
                    className={`px-4 py-2 rounded-md font-semibold ${activeAddressSection === 'address'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                      }`}
                  >
                    Address
                    {/* Indicate errors on the Address tab if needed */}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveAddressSection('billingAddress')}
                    className={`px-4 py-2 rounded-md font-semibold flex items-center ${activeAddressSection === 'billingAddress'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                      } ${hasBillingErrors ? 'border border-red-500' : ''}`}
                  >
                    Billing Address
                    {hasBillingErrors && (
                      <ErrorIcon
                        style={{ marginLeft: '0.5rem' }}
                        fontSize="small"
                        className="text-red-500"
                      />
                    )}
                  </button>
                </div>

                {activeAddressSection === 'address' && (
                  <>
                    <RenderFields
                      fields={AddressFields}
                      formData={buildingFormData}
                      handleChange={validatedHandleFieldChange}
                    />
                    {errors['addressLine1'] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors['addressLine1']}
                      </p>
                    )}
                    {errors['city'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['city']}</p>
                    )}
                    {errors['state'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['state']}</p>
                    )}
                    {errors['zip'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['zip']}</p>
                    )}
                  </>
                )}
                {activeAddressSection === 'billingAddress' && (
                  <>
                    <RenderFields
                      fields={BillingAddressFields}
                      formData={buildingFormData}
                      handleChange={validatedHandleFieldChange}
                    />
                    {errors['billingAddressLine1'] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors['billingAddressLine1']}
                      </p>
                    )}
                    {errors['billingCity'] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors['billingCity']}
                      </p>
                    )}
                    {errors['billingState'] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors['billingState']}
                      </p>
                    )}
                    {errors['billingZip'] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors['billingZip']}
                      </p>
                    )}
                  </>
                )}
              </div>
            </ContactAccordion>

            {/* Balance Information Section */}
            <ContactAccordion label="Balance Information" expanded={isExpanded}>
              <div className="rounded-lg p-4 bg-white">
                {(() => {
                  const [leftFields, rightFields] = splitFields(BalanceFields);
                  return (
                    <div className="flex gap-4">
                      <div className="w-full">
                        <RenderFields
                          fields={leftFields}
                          formData={buildingFormData}
                          handleChange={validatedHandleFieldChange}
                        />
                      </div>
                      <div className="w-full">
                        <RenderFields
                          fields={rightFields}
                          formData={buildingFormData}
                          handleChange={validatedHandleFieldChange}
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>
            </ContactAccordion>

            {/* Building Status Section */}
            <ContactAccordion label="Building Status" expanded={isExpanded}>
              <div className="rounded-lg p-4 bg-white">
                <RenderFieldsDropdown
                  fields={[
                    {
                      key: 'buildingStatus',
                      label: 'Building Status',
                      options: StatusOptions.map((status) => ({
                        key: status.key,
                        value: status.label,
                      })),
                    },
                    {
                      key: 'billingClass',
                      label: 'Billing Class',
                      options: BillingClassOptions.map((option) => ({
                        key: option.key,
                        value: option.label,
                      })),
                    },
                    {
                      key: 'dispatchType',
                      label: 'Dispatch Type',
                      options: DispatchTypes.map((option) => ({
                        key: option.key,
                        value: option.label,
                      })),
                    },
                  ]}
                  formData={buildingFormData}
                  handleChange={validatedHandleFieldChange}
                />

                {/* Remaining Building Status Fields */}
                {(() => {
                  const remainingFields = BuildingStatusFields.filter(
                    (field) =>
                      ![
                        'buildingStatus',
                        'billingClass',
                        'dispatchType',
                        'routeID',
                      ].includes(field.key)
                  );
                  const [leftFields, rightFields] = splitFields(remainingFields);
                  return (
                    <div className="flex gap-4">
                      <div className="w-full">
                        <RenderFields
                          fields={leftFields}
                          formData={buildingFormData}
                          handleChange={validatedHandleFieldChange}
                        />
                      </div>
                      <div className="w-full">
                        <RenderFields
                          fields={rightFields}
                          formData={buildingFormData}
                          handleChange={validatedHandleFieldChange}
                        />
                      </div>
                    </div>
                  );
                })()}

                {errors['buildingStatus'] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors['buildingStatus']}
                  </p>
                )}
                {errors['billingClass'] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors['billingClass']}
                  </p>
                )}
                {errors['dispatchType'] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors['dispatchType']}
                  </p>
                )}
                {errors['routeID'] && (
                  <p className="text-red-500 text-sm mt-1">{errors['routeID']}</p>
                )}

                {/*Route Form Section */}
                {isNewRouteDialogOpen && (
                  <div className="p-4 rounded-md border border-gray-300 bg-gray-100 mb-4">
                    <h3 className="text-lg font-semibold text-gray-600 mb-4">Add New Route</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {(() => {
                        const [leftFields, rightFields] = splitFields(RouteFields);
                        return (
                          <>
                            {/* Left Fields */}
                            <div className="flex flex-col w-full">
                              {leftFields.map((field) =>
                                field.type === 'checkbox' ? (
                                  <div key={field.key} className="flex items-center gap-2">
                                    <label htmlFor={field.key} className="font-semibold">
                                      {field.label}
                                    </label>
                                    <Checkbox
                                      id={field.key}
                                      name={field.key}
                                      checked={!!newRoute[field.key]} // Cast to boolean
                                      onChange={(e) =>
                                        newRouteHandleFieldChange({
                                          target: { name: field.key, value: e.target.checked },
                                        })
                                      }
                                    />
                                  </div>
                                ) : (
                                  <RenderFields
                                    key={field.key}
                                    fields={[field]} // Pass single field as an array
                                    formData={newRoute}
                                    handleChange={newRouteHandleFieldChange}
                                  />
                                )
                              )}
                            </div>
                            {/* Right Fields */}
                            <div className="flex flex-col w-full">
                              {rightFields.map((field) =>
                                field.type === 'checkbox' ? (
                                  <div key={field.key} className="flex items-center gap-2">
                                    <label htmlFor={field.key} className="font-semibold">
                                      {field.label}
                                    </label>
                                    <Checkbox
                                      id={field.key}
                                      name={field.key}
                                      checked={!!newRoute[field.key]} // Cast to boolean
                                      onChange={(e) =>
                                        newRouteHandleFieldChange({
                                          target: { name: field.key, value: e.target.checked },
                                        })
                                      }
                                    />
                                  </div>
                                ) : (
                                  <RenderFields
                                    key={field.key}
                                    fields={[field]} // Pass single field as an array
                                    formData={newRoute}
                                    handleChange={newRouteHandleFieldChange}
                                  />
                                )
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Form Buttons */}
                    <div className="flex justify-start mt-4">
                      <button
                        onClick={handleSaveRoute} // Save the route
                        className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition duration-300 ease-in-out mr-4"
                      >
                        Save
                      </button>
                      <button
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition duration-300 ease-in-out"
                        onClick={() => setIsNewRouteDialogOpen(false)} // Hide the form
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Route ID Dropdown */}
                <div className="flex items-center gap-2">
                  <label className="font-semibold mr-2">Route ID</label>
                  <select
                    name="routeID"
                    className="p-2 border rounded flex-1"
                    value={buildingFormData.routeID}

                    onChange={(e) => {
                      handleFieldChange({
                        target: { name: 'routeID', value: e.target.value },
                      });
                    }}

                  >
                    <option value="">Select Route</option>
                    {routeOptions.map((route) => (
                      <option key={route.key} value={route.key}>
                        {route.value}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setIsNewRouteDialogOpen(true)} // Open the "New" route form
                    // className="ml-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                    className="px-4 py-2 border rounded-md bg-sss-accent-green text-white font-semibold hover:border-sss-accent-yel"
                  >
                    New Route
                  </button>
                </div>
              </div>
            </ContactAccordion>

          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BuildingEntryForm;